import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Switch,
  FormControlLabel,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Avatar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Pagination,
  Grid,
  Alert,
  Tooltip,
  Container,
  TextField,
  InputAdornment,
} from '@mui/material';
import {
  Business as BusinessIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Search as SearchIcon,
  Image as ImageIcon,
} from '@mui/icons-material';
import { useAllSponsorSettings } from '../../hooks/useSponsorSettings';
import {
  CompanyWithSponsorSettings,
  UpdateSponsorSettingsRequest,
} from '../../services/sponsorService';

interface SponsorEditDialogProps {
  open: boolean;
  company: CompanyWithSponsorSettings | null;
  onClose: () => void;
  onSave: (
    companyId: number,
    settings: UpdateSponsorSettingsRequest
  ) => Promise<void>;
}

const SponsorEditDialog: React.FC<SponsorEditDialogProps> = ({
  open,
  company,
  onClose,
  onSave,
}) => {
  const [isSponsor, setIsSponsor] = useState(false);
  const [loading, setLoading] = useState(false);

  React.useEffect(() => {
    if (company) {
      setIsSponsor(company.isSponsor);
    }
  }, [company]);

  const handleSave = async () => {
    if (!company) return;

    setLoading(true);
    try {
      await onSave(company.id, {
        isSponsor,
      });
      onClose();
    } catch (error) {
      console.error('スポンサー設定の更新に失敗:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
        },
      }}
    >
      <DialogTitle
        sx={{
          pb: 1,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ bgcolor: 'primary.main' }}>
            <BusinessIcon />
          </Avatar>
          <Box>
            <Typography variant="h6" component="div">
              スポンサー設定
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {company?.companyName}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* スポンサー設定 */}
          <Card variant="outlined" sx={{ bgcolor: 'grey.50' }}>
            <CardContent>
              <FormControlLabel
                control={
                  <Switch
                    checked={isSponsor}
                    onChange={(e) => setIsSponsor(e.target.checked)}
                    color="primary"
                    disabled={loading}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body1" fontWeight={500}>
                      スポンサー企業として表示
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      学生のタイムラインにロゴが表示されます
                    </Typography>
                  </Box>
                }
              />
            </CardContent>
          </Card>

          <Alert severity="info">
            スポンサー表示のロゴは企業プロフィールで設定したロゴURLが利用されます。
          </Alert>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          p: 3,
          borderTop: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Button
          onClick={handleClose}
          disabled={loading}
          startIcon={<CancelIcon />}
        >
          キャンセル
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} /> : <SaveIcon />}
        >
          保存
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const SponsorManagement: React.FC = () => {
  const {
    companies,
    loading,
    error,
    pagination,
    searchQuery,
    setSearchQuery,
    updateCompanySponsorSettings,
    goToPage,
    search,
    resetSearch,
  } = useAllSponsorSettings();

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] =
    useState<CompanyWithSponsorSettings | null>(null);
  const [sponsorFilter, setSponsorFilter] = useState<string>('all');

  const handleEditClick = (company: CompanyWithSponsorSettings) => {
    setSelectedCompany(company);
    setEditDialogOpen(true);
  };

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setSelectedCompany(null);
  };

  const handleSaveSettings = async (
    companyId: number,
    settings: UpdateSponsorSettingsRequest
  ) => {
    await updateCompanySponsorSettings(companyId, settings);
  };

  const handleSearchKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      search();
    }
  };

  // フィルタリングされた企業リスト
  const filteredCompanies = companies.filter((company) => {
    const matchesSearch =
      !searchQuery ||
      company.companyName.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesSponsorFilter =
      sponsorFilter === 'all' ||
      (sponsorFilter === 'sponsor' && company.isSponsor) ||
      (sponsorFilter === 'non-sponsor' && !company.isSponsor);

    return matchesSearch && matchesSponsorFilter;
  });

  const sponsorCount = companies.filter((company) => company.isSponsor).length;

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          スポンサー管理
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          企業のスポンサー設定を管理します。スポンサー企業は学生のタイムラインに表示されます。
        </Typography>
      </Box>

      {/* エラー表示 */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* 統計情報 */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: 'primary.main' }}>
                  <StarIcon />
                </Avatar>
                <Box>
                  <Typography
                    variant="h4"
                    component="div"
                    sx={{ fontWeight: 700 }}
                  >
                    {sponsorCount}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    スポンサー企業
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: 'info.main' }}>
                  <BusinessIcon />
                </Avatar>
                <Box>
                  <Typography
                    variant="h4"
                    component="div"
                    sx={{ fontWeight: 700 }}
                  >
                    {companies.length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    総企業数
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* 検索・フィルター */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          検索・フィルター
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              placeholder="企業名で検索..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={handleSearchKeyPress}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>スポンサー状態</InputLabel>
              <Select
                value={sponsorFilter}
                label="スポンサー状態"
                onChange={(e) => setSponsorFilter(e.target.value)}
              >
                <MenuItem value="all">すべて</MenuItem>
                <MenuItem value="sponsor">スポンサー企業のみ</MenuItem>
                <MenuItem value="non-sponsor">一般企業のみ</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                onClick={search}
                disabled={loading}
                fullWidth
              >
                検索
              </Button>
              <Button
                variant="outlined"
                onClick={() => {
                  resetSearch();
                  setSponsorFilter('all');
                }}
                disabled={loading}
              >
                リセット
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* 企業一覧テーブル */}
      <TableContainer component={Paper}>
        {/* 検索結果表示 */}
        {(searchQuery || sponsorFilter !== 'all') && (
          <Box
            sx={{
              p: 2,
              borderBottom: '1px solid',
              borderColor: 'divider',
              bgcolor: 'grey.50',
            }}
          >
            <Typography variant="body2" color="text.secondary">
              {filteredCompanies.length}件の企業が見つかりました
            </Typography>
          </Box>
        )}
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>企業名</TableCell>
              <TableCell>業種</TableCell>
              <TableCell align="center">スポンサー</TableCell>
              <TableCell>ロゴ</TableCell>
              <TableCell>更新日時</TableCell>
              <TableCell align="center">操作</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : filteredCompanies.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <Typography variant="body1" color="text.secondary">
                    企業が見つかりませんでした
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredCompanies.map((company) => (
                <TableRow key={company.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar sx={{ width: 32, height: 32 }}>
                        <BusinessIcon />
                      </Avatar>
                      <Typography variant="body1" fontWeight={500}>
                        {company.companyName}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {company.businessType || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      icon={
                        company.isSponsor ? <StarIcon /> : <StarBorderIcon />
                      }
                      label={company.isSponsor ? 'スポンサー' : '一般'}
                      color={company.isSponsor ? 'primary' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {company.logoUrl ? (
                      <Avatar
                        src={company.logoUrl}
                        sx={{ width: 32, height: 32 }}
                      >
                        <ImageIcon />
                      </Avatar>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        未設定
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {new Date(company.updatedAt).toLocaleDateString('ja-JP')}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="スポンサー設定を編集">
                      <IconButton
                        onClick={() => handleEditClick(company)}
                        size="small"
                        color="primary"
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>

        {/* ページネーション */}
        {pagination.totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <Pagination
              count={pagination.totalPages}
              page={pagination.page}
              onChange={(_, page) => goToPage(page)}
              color="primary"
              disabled={loading}
            />
          </Box>
        )}
      </TableContainer>

      {/* 編集ダイアログ */}
      <SponsorEditDialog
        open={editDialogOpen}
        company={selectedCompany}
        onClose={handleCloseEditDialog}
        onSave={handleSaveSettings}
      />
    </Container>
  );
};
